import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Spin, Alert, Tag } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import './HighFidelityYieldChart.css';

const HighFidelityYieldChart = ({ 
  apiData, 
  isLoading, 
  error, 
  topTrades, 
  tradeDate,
  title,
  description,
  type = 'Corporate',
  color = '#0f172a',
  maxYear,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const chartId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  const chartData = useMemo(() => {
    let raw = [];
    if (Array.isArray(apiData)) raw = apiData;
    else if (apiData && Array.isArray(apiData.yieldData)) raw = apiData.yieldData;
    
    return raw.map(item => ({
      ...item,
      year: Number(item.year),
      yield: Number(item.yield)
    }))
    .filter(item => !isNaN(item.year) && !isNaN(item.yield))
    .filter(item => maxYear == null || item.year <= maxYear)
    .sort((a, b) => a.year - b.year);
  }, [apiData, maxYear]);

  const yieldAxisBounds = useMemo(() => {
    const raw = chartData;
    if (raw.length === 0) return { min: 5.5, max: 8.5 };
    const vals = raw.map(v => v.yield);
    const dataMin = Math.min(...vals);
    const dataMax = Math.max(...vals);
    return { 
      min: Math.floor((dataMin - 0.2) * 10) / 10, 
      max: Math.ceil((dataMax + 0.2) * 10) / 10 
    };
  }, [chartData]);

  const yearAxisBounds = useMemo(() => {
    const raw = chartData;
    const defaultMax = type === 'G-Sec' ? 40 : (type === 'SDL' ? 15 : 15);
    const defaultBounds = { min: 0, max: defaultMax };
    
    if (raw.length === 0) return defaultBounds;
    const years = raw.map(v => v.year);
    
    const dataMax = Math.max(...years);
    let standardMax = defaultMax;
    
    if (type === 'G-Sec') {
      if (dataMax <= 15) standardMax = 15;
      else if (dataMax <= 30) standardMax = 30;
      else if (dataMax <= 40) standardMax = 40;
      else standardMax = Math.ceil(dataMax / 10) * 10;
    } else if (type === 'SDL') {
      if (dataMax <= 10) standardMax = 10;
      else if (dataMax <= 15) standardMax = 15;
      else if (dataMax <= 20) standardMax = 20;
      else standardMax = Math.ceil(dataMax / 5) * 5;
    } else {
      // Corporate
      const cap = maxYear != null ? maxYear : null;
      if (cap != null) {
        standardMax = cap;
      } else if (dataMax <= 10) standardMax = 10;
      else if (dataMax <= 15) standardMax = 15;
      else standardMax = Math.ceil(dataMax / 5) * 5;
    }

    return { 
      min: 0, 
      max: standardMax 
    };
  }, [chartData, type, maxYear]);

  const svgElements = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const min = yieldAxisBounds.min;
    const max = yieldAxisBounds.max;
    const range = max - min;

    const yMin = yearAxisBounds.min;
    const yMax = yearAxisBounds.max;
    const yRange = yMax - yMin || 1;

    const points = chartData
      .filter(item => item.yield !== undefined)
      .map((item) => {
        const x = 50 + ((item.year - yMin) / yRange) * 900;
        const y = 410 - ((item.yield - min) / range) * 350;
        return { x, y, year: item.year, yield: item.yield };
      });

    if (points.length < 1) return null;

    // Generate smooth path
    let d = '';
    if (points.length >= 2) {
      d = `M ${points[0].x},${points[0].y}`;
      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];
        const cp1x = p1.x + (p2.x - p1.x) / 2;
        const cp1y = p1.y;
        const cp2x = p1.x + (p2.x - p1.x) / 2;
        const cp2y = p2.y;
        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }
    }

    const fillD = points.length >= 2 ? `${d} L ${points[points.length - 1].x},450 L ${points[0].x},450 Z` : '';

    // Highlight points
    const shortEnd = points.find(p => p.year >= 2) || points[0];
    const belly = points.find(p => p.year >= 7) || points[Math.floor(points.length / 2)];
    const longEnd = points[points.length - 1];

    return {
      d,
      fillD,
      allPoints: points,
      highlights: [
        { ...shortEnd, label: 'Short End', pos: 'above' },
        { ...belly, label: 'Belly', pos: 'below' },
        { ...longEnd, label: 'Long End', pos: 'above' }
      ]
    };
  }, [chartData, yieldAxisBounds, yearAxisBounds]);

  // Generate Y-axis labels based on bounds
  const yTicks = useMemo(() => {
    const { min, max } = yieldAxisBounds;
    const ticks = [];
    const step = (max - min) / 4;
    for (let i = 0; i <= 4; i++) {
      const val = min + i * step;
      ticks.push({
        val: val.toFixed(2),
        y: 410 - ((val - min) / (max - min)) * 350
      });
    }
    return ticks;
  }, [yieldAxisBounds]);

  // Generate X-axis labels
  const xLabels = useMemo(() => {
    const { min, max } = yearAxisBounds;
    const labels = [];
    const count = 7;
    const step = (max - min) / (count - 1);
    
    for (let i = 0; i < count; i++) {
      const year = min + i * step;
      labels.push({
        year: `${year.toFixed(year < 10 ? 1 : 0)}Y`,
        x: 50 + ((year - min) / (max - min || 1)) * 900
      });
    }
    return labels;
  }, [yearAxisBounds]);

  // Hide empty charts — need at least one valid yield point
  if (!isLoading && !error && chartData.length < 1) {
    return null;
  }

  return (
    <div className="hf-yield-chart">
      <Row gutter={[12, 12]} align="stretch">
        <Col xs={24} lg={topTrades && topTrades.length > 0 ? 16 : 24} className="hf-yield-chart-col">
          <div className="sb-yc-outer-wrapper">
            {title && (
              <div className="hf-yield-header">
                <h3 className="hf-yield-title" style={{ color }}>{title}</h3>
                {description && <p className="hf-yield-desc">{description}</p>}
              </div>
            )}
            <div className="sb-yc-layout-grid">
              
              {/* Y-Axis Area (Left) */}
              <div className="sb-yc-y-area">
                <div className="sb-yc-y-title">YIELD (%)</div>
                <div className="sb-yc-y-labels">
                  {yTicks.map((tick, i) => (
                    <div 
                      key={`y-ext-${i}`} 
                      className="sb-yc-y-label"
                      style={{ top: `${(tick.y / 450) * 100}%` }}
                    >
                      {tick.val}
                    </div>
                  ))}
                </div>
              </div>

              {/* Chart Area (Top Right) */}
              <div className="sb-yc-inner-card">
                {isLoading ? (
                  <div className="chart-loading">
                    <Spin size="large" />
                    <p>Loading market data...</p>
                  </div>
                ) : error ? (
                  <div className="chart-error">
                    <Alert message="Error" description={error} type="error" showIcon />
                  </div>
                ) : svgElements ? (
                  <svg className="sb-yc-svg" viewBox="0 0 1000 450" preserveAspectRatio="none">
                    <defs>
                      <filter id={`line-shadow-${chartId}`} x="-10%" y="-10%" width="120%" height="120%">
                        <feDropShadow dx="0" dy="8" stdDeviation="6" floodColor={color} floodOpacity="0.15" />
                      </filter>
                      <linearGradient id={`area-gradient-${chartId}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                        <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                      </linearGradient>
                      <clipPath id={`shadow-clip-${chartId}`}>
                        <rect 
                          x="0" 
                          y="0" 
                          height="450" 
                          width={hoveredPoint ? hoveredPoint.x : 1000}
                          style={{ transition: 'width 0.8s cubic-bezier(0.23, 1, 0.32, 1)' }} 
                        />
                      </clipPath>
                    </defs>
                    {/* Grid Lines inside SVG */}
                    {yTicks.map((tick, i) => (
                      <line 
                        key={`grid-${i}`} 
                        x1="0" 
                        y1={tick.y} 
                        x2="1000" 
                        y2={tick.y} 
                        stroke="#0f172a" 
                        strokeOpacity="0.05"
                        strokeWidth="1" 
                      />
                    ))}
                    {/* Area Fill */}
                    {svgElements.fillD && (
                      <motion.path
                        d={svgElements.fillD}
                        fill={`url(#area-gradient-${chartId})`}
                        transform="translate(0, 14)"
                        clipPath={`url(#shadow-clip-${chartId})`}
                        style={{ pointerEvents: 'none' }}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                      />
                    )}
                    {/* Only Data Points in SVG */}
                    {svgElements.allPoints.map((p, i) => {
                      const isHovered = hoveredPoint && hoveredPoint.year === p.year;
                      return (
                        <g 
                          key={`pt-${i}`}
                          onMouseEnter={() => setHoveredPoint(p)}
                          onMouseLeave={() => setHoveredPoint(null)}
                          style={{ cursor: 'pointer' }}
                        >
                          <circle cx={p.x} cy={p.y} r="50" fill="transparent" />
                          
                          {isHovered ? (
                            <>
                              <circle cx={p.x} cy={p.y} r="14" fill="#ffffff" stroke="#000000" strokeWidth="3" style={{ pointerEvents: 'none' }} />
                              <circle cx={p.x} cy={p.y} r="6" fill="#000000" style={{ pointerEvents: 'none' }} />
                            </>
                          ) : (
                            <circle cx={p.x} cy={p.y} r="6" fill="#000000" style={{ pointerEvents: 'none' }} />
                          )}
                        </g>
                      );
                    })}
                    {/* Yield Curve Line - Moved to last to have highest Z-index */}
                    {svgElements.d && (
                      <motion.path
                        d={svgElements.d}
                        fill="none"
                        stroke={color}
                        strokeWidth="4"
                        strokeLinecap="round"
                        filter={`url(#line-shadow-${chartId})`}
                        style={{ pointerEvents: 'none' }}
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 2.5, ease: "easeInOut" }}
                      />
                    )}
                  </svg>
                ) : null}

                {/* Tooltip */}
                {hoveredPoint && (
                  <div 
                    className="sb-yc-tooltip"
                    style={{
                      left: `${(hoveredPoint.x / 1000) * 100}%`,
                      top: `${(hoveredPoint.y / 450) * 100}%`
                    }}
                  >
                    <span className="sb-yc-tooltip-year">{hoveredPoint.year}Y</span>
                    <span className="sb-yc-tooltip-yield">{hoveredPoint.yield.toFixed(2)}%</span>
                  </div>
                )}
              </div>

              {/* X-Axis Area (Bottom Right) */}
              <div className="sb-yc-x-area">
                <div className="sb-yc-x-labels">
                  {xLabels.map((lb, i) => (
                    <div 
                      key={`x-ext-${i}`} 
                      className="sb-yc-x-label"
                      style={{ left: `${(lb.x / 1000) * 100}%` }}
                    >
                      {lb.year}
                    </div>
                  ))}
                </div>
                <div className="sb-yc-x-title">MATURITY (YEARS)</div>
              </div>
            </div>
          </div>
        </Col>

        {topTrades && topTrades.length > 0 && (
          <Col xs={24} lg={8} className="top-trades-col">
            <div className="top-trades-col-inner">
              <Card
                className="top-trades-card top-trades-card-side"
                styles={{ body: { padding: '10px', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } }}
              >
                <div className="top-trades-header compact">
                  <div className="top-trades-title">
                    <span className="live-dot" aria-hidden />
                    <span className="title-text">Market Leaders</span>
                    <Tag className="trades-count-tag">{Math.min(10, topTrades.length)}</Tag>
                  </div>
                  {tradeDate && (
                    <div className="top-trades-date-pill">
                      <ClockCircleOutlined />
                      <span>{tradeDate}</span>
                    </div>
                  )}
                </div>

                <div className="top-trades-mini-list" role="list">
                  {topTrades.slice(0, 10).map((trade, idx) => {
                    const valueText =
                      typeof trade?.totalFaceValue === 'number'
                        ? `${(trade.totalFaceValue / 10000000).toFixed(2)} Cr`
                        : typeof trade?.totalTradeValue === 'number'
                          ? `${trade.totalTradeValue.toFixed(2)} Cr`
                          : '—';
                    return (
                      <div
                        key={trade?.isin || `trade-${idx}`}
                        className="top-trades-mini-card"
                        role="listitem"
                      >
                        <div className={`top-trades-rank rank-${idx + 1}`}>{idx + 1}</div>
                        <div className="top-trades-content">
                          <div className="top-trades-mini-issuer" title={trade?.securityDescription || trade?.issuerDescription || ''}>
                            {trade?.securityDescription || trade?.issuerDescription || '—'}
                          </div>
                          <div className="top-trades-mini-row">
                            <span className="top-trades-mini-isin">{trade?.isin || '—'}</span>
                            <span className="value-amount">{valueText}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default HighFidelityYieldChart;
